import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, forkJoin, of, switchMap, map } from 'rxjs';
import { ProjectsService } from 'src/app/services/projects.service';
import { ProjectNotesProvider } from 'src/app/services/project-notes-provider.service';
import { CommentsComponent } from '@shared/components/comments/comments.component';
import { ProjectLookupsDto, RigLookupDto, WellLookupDto } from '@models/project-lookups-dto.interface';
import { SaveProjectDto, SelectionMode } from '@models/save-project-dto.interface';

export interface EditProjectDialogData {
  mode: 'create' | 'edit';
  projectId?: number;
}

@Component({
  selector: 'app-edit-project',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    CommentsComponent,
  ],
  templateUrl: './edit-project.component.html',
  styleUrl: './edit-project.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditProjectComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<EditProjectComponent>);
  protected readonly data = inject<EditProjectDialogData>(MAT_DIALOG_DATA);
  private readonly projectsService = inject(ProjectsService);
  private readonly fb = inject(FormBuilder);
  protected readonly projectNotesProvider = inject(ProjectNotesProvider);
  private readonly destroyRef = inject(DestroyRef);

  protected isLoading = signal(true);
  protected isSaving = signal(false);
  protected errorMessage = signal('');
  protected isCreateMode = this.data.mode === 'create';
  protected pendingComment = signal('');

  protected lookups = signal<ProjectLookupsDto>({
    businessUnits: [],
    rigs: [],
    wells: [],
    suppliers: [],
  });

  protected selectedRigId = signal<number | null>(null);

  protected filteredWells = computed(() => {
    const rigId = this.selectedRigId();
    const wells = this.lookups().wells;
    if (!rigId) {
      return wells;
    }
    return wells.filter(w => w.defaultRigId === rigId);
  });

  protected form = this.fb.group(
    {
      rigId:          [null as number | null],
      wellId:         [null as number | null],
      projectName:    [null as string | null, Validators.maxLength(200)],
      businessUnitId: [0, [Validators.required, Validators.min(1)]],
      isTurnKey:      [false, Validators.required],
      initiatorsCai:  [null as string | null, Validators.maxLength(100)],
      sapCode:        [null as string | null],
    },
    { validators: projectFormValidator }
  );

  ngOnInit(): void {
    this.setupAutoSelection();

    const lookups$ = this.projectsService.getLookups();
    const project$ = !this.isCreateMode && this.data.projectId
      ? this.projectsService.getForEdit(this.data.projectId)
      : of(null);

    forkJoin([lookups$, project$]).subscribe({
      next: ([lookups, project]) => {
        this.lookups.set(lookups);

        if (project) {
          this.form.patchValue({
            rigId:          project.rigId,
            wellId:         project.wellId,
            projectName:    project.projectName,
            businessUnitId: project.businessUnitId,
            isTurnKey:      project.isTurnKey,
            initiatorsCai:  project.initiatorsCai,
            sapCode:        project.sapCode,
          });
          this.selectedRigId.set(project.rigId);
        }

        this.isLoading.set(false);

        if (this.isCreateMode) {
          this.autoSetSubsurfaceIfNoRigWell();
        }
      },
      error: () => this.isLoading.set(false),
    });
  }

  private setupAutoSelection(): void {  
    this.form.get('rigId')!.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((rigId) => {
      if (this.isLoading()) return;
      this.onRigChanged(rigId);
    });
  }
  

  private onRigChanged(rigId: number | null): void {
    this.selectedRigId.set(rigId);

    const wellId = this.form.get('wellId')!.value;
    if (wellId && !this.filteredWells().some(w => w.id === wellId)) {
      this.form.get('wellId')!.setValue(null, { emitEvent: false });
    }

    if (rigId) {
      const rig = this.lookups().rigs.find(r => r.id === rigId);
      if (rig?.defaultBusinessUnitId) {
        this.form.get('businessUnitId')!.setValue(rig.defaultBusinessUnitId, { emitEvent: false });
        return;
      }
    }
    this.autoSetSubsurfaceIfNoRigWell();
  }

  private autoSetSubsurfaceIfNoRigWell(): void {
    const rigId = this.form.get('rigId')!.value;
    const wellId = this.form.get('wellId')!.value;
    if (!rigId && !wellId) {
      const subsurface = this.lookups().businessUnits.find(
        bu => bu.name?.toLowerCase() === 'subsurface'
      );
      if (subsurface) {
        this.form.get('businessUnitId')!.setValue(subsurface.id, { emitEvent: false });
      }
    }

  }

  protected rigDisplayName(rig: RigLookupDto): string {
    return rig.alias ? `${rig.name} (${rig.alias})` : (rig.name ?? '');
  }

  protected wellDisplayName(well: WellLookupDto): string {
    return well.name ?? '';
  }

  protected get hasWell(): boolean {
    return !!this.form.get('wellId')?.value;
  }

  protected onSave(): void {
    if (this.form.invalid) return;

    const raw = this.form.getRawValue();
    const selectedRig = raw.rigId
      ? (this.lookups().rigs.find(r => r.id === raw.rigId) ?? null)
      : null;
    const selectedWell = raw.wellId
      ? (this.lookups().wells.find(w => w.id === raw.wellId) ?? null)
      : null;

    const dto: SaveProjectDto = {
      rigSelection: raw.rigId ? SelectionMode.Existing : SelectionMode.None,
      rigId:        raw.rigId,
      rigName:      selectedRig?.name ?? null,
      rigAlias:     selectedRig?.alias ?? null,

      wellSelection:   raw.wellId ? SelectionMode.Existing : SelectionMode.None,
      wellId:          raw.wellId,
      wellName:        selectedWell?.name ?? null,
      prospectName:    null,
      prospectNumber:  null,
      field:           null,
      blockNumber:     null,
      api:             null,
      chevNo:          null,
      mudSupplierId:   null,
      cementSupplierId:null,

      projectName:    raw.projectName,
      businessUnitId: Number(raw.businessUnitId),
      isTurnKey:      Boolean(raw.isTurnKey),
      initiatorsCai:  raw.initiatorsCai,
      sapCode:        raw.sapCode,
    };

    this.isSaving.set(true);
    this.errorMessage.set('');

    const save$: Observable<unknown> = this.isCreateMode
      ? this.projectsService.create(dto).pipe(
          switchMap((newId: number) => {
            const comment = this.pendingComment().trim();
            return comment
              ? this.projectNotesProvider.addNote(newId, comment).pipe(map(() => newId))
              : of(newId);
          })
        )
      : this.projectsService.update(this.data.projectId!, dto);

    save$.subscribe({
      next: (result) => {
        this.isSaving.set(false);
        this.dialogRef.close(this.isCreateMode ? result : true);
      },
      error: (err: HttpErrorResponse) => {
        this.isSaving.set(false);
        this.errorMessage.set(this.extractErrorMessage(err));
      },
    });
  }

  private extractErrorMessage(err: HttpErrorResponse): string {
    return err.error?.detail || 'Save failed. Please check your entries and try again.';
  }

  protected onCancel(): void {
    this.dialogRef.close(false);
  }
}

function projectFormValidator(control: AbstractControl): ValidationErrors | null {
  const wellId = control.get('wellId')?.value as number | null;

  if (!wellId && !control.get('projectName')?.value?.trim()) {
    return { projectNameRequired: true };
  }

  return null;
}
